import useTranslation from 'next-translate/useTranslation'
import WarningIcon from '@ancon/wildcat-ui/shared/icons/warning-outline.svg'
import noop from 'lodash/noop'

import BodyText from '../../app/components/BodyText'
import Button from '../../app/components/Button'
import HeadingText from '../../app/components/HeadingText'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import Modal from '../../app/components/modal/Modal'
import useAppSelector from '../../../store/hooks/useAppSelector'
import { walletCheckoutWarningModalVisibleSelector } from '../store/walletsSelectors'
import {
  setWalletCheckoutWarningModalVisible,
  setWalletTopUpModalVisible,
} from '../store/walletsSlice'
import { WalletCheckoutWarningType } from '../types'
import { deleteCheckout } from '../../checkout/store/checkoutThunks'
import { setProductDetailsModalVisible } from '../../product/store/productSlice'
import { checkoutDeletePendingSelector } from '../../checkout/store/checkoutSelectors'

import styles from './WalletCheckoutWarningModal.module.scss'

interface WalletCheckoutWarningModalProps {
  type: WalletCheckoutWarningType
}

function WalletCheckoutWarningModal({ type }: WalletCheckoutWarningModalProps) {
  const { t } = useTranslation('wallets')

  const dispatch = useAppDispatch()

  const isVisible = useAppSelector(walletCheckoutWarningModalVisibleSelector)

  const isCheckoutDeletePending = useAppSelector(checkoutDeletePendingSelector)

  function handleCloseModal() {
    if (!isCheckoutDeletePending) {
      dispatch(setWalletCheckoutWarningModalVisible(false))
    }
  }

  function handleDeleteCheckoutSuccess() {
    switch (type) {
      case WalletCheckoutWarningType.AddProducts:
        dispatch(setProductDetailsModalVisible(true))
        break
      case WalletCheckoutWarningType.WalletTopUp:
      default:
        dispatch(setWalletTopUpModalVisible(true))
        break
    }
    dispatch(setWalletCheckoutWarningModalVisible(false))
  }

  function handleStartNewOrder() {
    dispatch(deleteCheckout())
      .unwrap()
      .then(handleDeleteCheckoutSuccess)
      .catch(noop)
  }

  return (
    <Modal
      isOpen={isVisible}
      onClose={handleCloseModal}
      mobileModalMode="bottom-sheet"
      contentClassName={styles.modalContent}
      hideClose
    >
      <div className={styles.modalHeader}>
        <WarningIcon />
        <HeadingText as="h2">{t('checkoutWarningModal.title')}</HeadingText>
      </div>
      <BodyText color="body-1">
        {t(`checkoutWarningModal.message.${type}`)}
      </BodyText>
      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          size="large"
          onClick={handleCloseModal}
          disabled={isCheckoutDeletePending}
        >
          {t('checkoutWarningModal.keepExistingOrder')}
        </Button>
        <Button
          size="large"
          loading={isCheckoutDeletePending}
          onClick={handleStartNewOrder}
        >
          {t('checkoutWarningModal.startNewOrder')}
        </Button>
      </div>
    </Modal>
  )
}

export default WalletCheckoutWarningModal
